import { useEffect, useMemo, useState } from 'react'
import useSWR from 'swr'
import { useContractReads } from 'wagmi'
import { DEX_NAMES, MULTISWAP_ENABLED } from 'multiswap/config'
import { ROUTE_COMPARE_ENABLED } from 'multiswap/enabled'
import { cad1LauncherAbi } from 'views/Cad1/abi/cad1Launcher'
import { CAD1_CHAIN_ID, CAD1_LIVE, CAD1_PUBLIC_ENV, CAD1_VISIBLE, getCad1Config, resolveCad1Config } from 'views/Cad1/config'
import { useLockFees } from 'views/LockTools/fees'
import type { LockPage } from 'views/LockTools/types'
import { useTokenList, useTokenPagesSettings } from 'views/TokenPages/hooks'
import type { CompetitionRow } from 'views/TradingCompetitionV3/components/CompetitionCard'
import { V3_API_URL, fetcher } from 'views/TradingCompetitionV3/config'
import { competitionV4Abi } from 'views/TradingCompetitionV4/abi'
import { V4_ADDRESS, V4_API_URL, V4_CHAIN_ID, V4_ENABLED } from 'views/TradingCompetitionV4/config'
import { ApiFee, Badge, FeeLine, PageLine, competitionFee, launchBadge, lockBadge, pickCompetitions, pickHotTokens, sourcesLine, swapBadge, tokenPageLine } from './logic'

/*
 * The home's live reads. Every source is the one its own page already reads, so the home never states a different number:
 *   - lock fees: both lockers' priceInNative / priceInCbon (views/LockTools/fees.ts, the lock form's own read);
 *   - launch fee: the CAD1 launcher's creationFee() plus the V3 lock's priceInNative (what /cad1 charges), only while CAD1 is open;
 *   - competition fee: V4 feeConfig(0) on chain (the competitions list reads the same), /api/competitions/v4/config only when that read fails;
 *   - token page: /api/token-pages/settings (the submit form's and the ticks panel's read); the swap: its own DEX list (multiswap/config);
 *   - the board: /api/competitions/v{4,3}/competitions, /api/tokens/list, /api/locks?sort=newest.
 * All of them load after the first paint; the components hold their size with skeletons while they do.
 */
const quiet = { revalidateOnFocus: false, shouldRetryOnError: false }

/** The CAD1 launcher address resolved the way /cad1 resolves it (build env first, the Stage test seam after mount), without /cad1's heavy hooks. */
function useCad1Launcher(): { launcher: string; ready: boolean } {
  const [state, setState] = useState(() => ({ launcher: resolveCad1Config(CAD1_PUBLIC_ENV).launcher as string, ready: false }))
  useEffect(() => {
    setState({ launcher: getCad1Config().launcher as string, ready: true })
  }, [])
  return state
}

export type ToolLines = {
  swap: { badge: Badge; sub: string | null }
  lock: { badge: Badge; sub: string | null }
  compete: FeeLine
  pages: PageLine
  launch: { badge: Badge }
}

export function useToolLines(): ToolLines {
  const v3 = useLockFees('v3')
  const v2 = useLockFees('v2')
  const chain = useContractReads({
    contracts: [{ address: V4_ADDRESS || undefined, abi: competitionV4Abi, functionName: 'feeConfig', args: [0], chainId: V4_CHAIN_ID }] as any,
    enabled: V4_ENABLED,
    staleTime: 30_000,
    watch: false,
  } as any) as { data?: Array<{ status: string; result?: readonly [bigint, bigint, boolean] }>; isLoading: boolean; isError: boolean }
  const row = chain.data?.[0]
  const chainFee = useMemo(
    () => (row?.status === 'success' && row.result ? { cost: row.result[0], costWithReferral: row.result[1], enabled: row.result[2] } : undefined),
    [row],
  )
  const chainFailed = V4_ENABLED && !chain.isLoading && !chainFee && (chain.isError || chain.data !== undefined)
  // the API's copy of the same on-chain fee, asked only when the chain read failed (same SWR key as the create page's discount terms)
  const api = useSWR<{ fees?: { bnb?: ApiFee } }>(chainFailed ? `${V4_API_URL}/config` : null, fetcher, quiet)
  const settings = useTokenPagesSettings()
  const cad1 = useCad1Launcher()
  const cad1Open = CAD1_VISIBLE && CAD1_LIVE && cad1.ready && Boolean(cad1.launcher)
  const launchRead = useContractReads({
    contracts: [{ address: cad1.launcher || undefined, abi: cad1LauncherAbi, functionName: 'creationFee', chainId: CAD1_CHAIN_ID }] as any,
    enabled: cad1Open,
    staleTime: 30_000,
    watch: false,
  } as any) as { data?: Array<{ status: string; result?: bigint }>; isLoading: boolean; isError: boolean }
  const launchRow = launchRead.data?.[0]
  const creationFee = launchRow?.status === 'success' && typeof launchRow.result === 'bigint' ? launchRow.result : undefined
  const launchFailed = cad1Open && !launchRead.isLoading && creationFee === undefined && (launchRead.isError || launchRead.data !== undefined)

  return useMemo(() => {
    const lock = lockBadge([v3, v2])
    const sources = MULTISWAP_ENABLED[56] ?? []
    return {
      swap: { badge: swapBadge(sources, ROUTE_COMPARE_ENABLED), sub: ROUTE_COMPARE_ENABLED ? sourcesLine(sources.map((d) => DEX_NAMES[d] ?? d)) : null },
      lock: { badge: lock, sub: lock.kind === 'free' ? 'You pay only gas' : null },
      compete: competitionFee({ configured: V4_ENABLED, chain: chainFee, chainLoading: V4_ENABLED && chain.isLoading, chainFailed, api: api.data?.fees?.bnb ?? null, apiLoading: api.isLoading }),
      pages: tokenPageLine(settings.data as any, settings.isLoading),
      launch: { badge: launchBadge({ open: cad1Open, creationFee, feeLoading: cad1Open && launchRead.isLoading, feeFailed: launchFailed, lock: v3 }) },
    }
  }, [v3, v2, chainFee, chain.isLoading, chainFailed, api.data, api.isLoading, settings.data, settings.isLoading, cad1Open, creationFee, launchRead.isLoading, launchFailed])
}

/** Live and upcoming competitions on both contracts (the competitions list's own two requests, so SWR shares them). */
export function useBoardCompetitions(nowSec: number | null) {
  const cur = useSWR<{ results: CompetitionRow[] }>(V4_ENABLED ? `${V4_API_URL}/competitions?status=all` : `${V3_API_URL}/competitions?status=all`, fetcher, { ...quiet, refreshInterval: 60_000 })
  const old = useSWR<{ results: CompetitionRow[] }>(V4_ENABLED ? `${V3_API_URL}/competitions?status=all` : null, fetcher, { ...quiet, refreshInterval: 120_000 })
  const picked = useMemo(() => pickCompetitions(cur.data?.results, old.data?.results, nowSec ?? Math.floor(Date.now() / 1000)), [cur.data, old.data, nowSec])
  return { ...picked, loading: !cur.data && !cur.error, failed: Boolean(cur.error) && !cur.data }
}

export function useHotTokens(max = 5) {
  const r = useTokenList()
  const rows = useMemo(() => pickHotTokens(r.data?.items, max), [r.data, max])
  return { rows, all: r.data?.items ?? [], loading: !r.data && !r.error, failed: Boolean(r.error) && !r.data }
}

export function useRecentLocks(max = 4) {
  const r = useSWR<LockPage>('/api/locks?sort=newest', fetcher, { ...quiet, refreshInterval: 60_000 })
  const rows = useMemo(() => (r.data?.items ?? []).slice(0, max), [r.data, max])
  return { rows, totals: r.data?.totals ?? null, loading: !r.data && !r.error, failed: Boolean(r.error) && !r.data }
}
